// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/onboarding/sonidos.js
//  EL SONIDO DEL DIAGNÓSTICO — SINTETIZADO, SIN UN SOLO FICHERO DE AUDIO
//
//  Rob, 24-09-2026: «no te olvides de poner sonidos, eso da un aspecto
//  inteligente y tecnológico».
//
//  ── POR QUÉ SINTETIZADO Y NO .mp3 ───────────────────────────────────────────
//  Un paquete de seis sonidos decentes son 150-400 KB que hay que descargar
//  antes de que suene el primero. En la puerta de entrada de NSL —donde el
//  visitante decide en segundos si se queda— eso es peso puro, y encima el
//  primer toque sonaría tarde o no sonaría. La Web Audio API los genera en el
//  momento: cero bytes de red, cero latencia, y se afinan cambiando un número.
//
//  ── LAS REGLAS QUE SE RESPETAN ──────────────────────────────────────────────
//  · Nada suena solo. El contexto de audio se crea en el PRIMER toque del
//    visitante, que es además lo único que los navegadores permiten: un
//    AudioContext creado antes de un gesto nace bloqueado.
//  · Se puede apagar, y se recuerda. El interruptor vive en el header, junto al
//    de tema, y la preferencia queda en `localStorage`.
//  · Volumen bajo por defecto (0.14 del máximo). Un sonido de interfaz que se
//    nota es un sonido mal puesto.
//  · Nada de esto puede tumbar la pantalla: si el navegador no tiene Web Audio
//    o el almacenamiento está bloqueado, todo falla en silencio y el
//    diagnóstico sigue funcionando igual.
//  · El error NO suena a error. El tono grave y corto de `fallo` mantiene el
//    principio del documento maestro: «no es un castigo».
// ═══════════════════════════════════════════════════════════════════════════════

const LLAVE = 'nsl_sonido';
const VOLUMEN_MAESTRO = 0.14;

let contexto = null;
let maestro = null;
let activo = leePreferencia();
const oyentes = new Set();

/* ── La preferencia ───────────────────────────────────────────────────────── */

function leePreferencia() {
    try {
        // Por defecto ENCENDIDO: el sonido es parte de la primera impresión, y
        // quien no lo quiera lo apaga de un toque.
        return localStorage.getItem(LLAVE) !== '0';
    } catch {
        return true;
    }
}

export function sonidoActivo() {
    return activo;
}

export function alternaSonido() {
    activo = !activo;
    try {
        localStorage.setItem(LLAVE, activo ? '1' : '0');
    } catch { /* modo privado: la preferencia dura lo que la pestaña */ }
    for (const avisa of oyentes) avisa(activo);
    // Confirmación audible al encender: sin ella el interruptor es un acto de fe.
    if (activo) toque();
    return activo;
}

/** Suscripción para que el botón del header se entere de los cambios. */
export function escuchaSonido(avisa) {
    oyentes.add(avisa);
    return () => oyentes.delete(avisa);
}

/* ── El motor ─────────────────────────────────────────────────────────────── */

function motor() {
    if (!activo) return null;
    try {
        if (!contexto) {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return null;
            contexto = new Ctx();
            maestro = contexto.createGain();
            maestro.gain.value = VOLUMEN_MAESTRO;
            maestro.connect(contexto.destination);
        }
        // Tras volver de otra pestaña el contexto queda suspendido.
        if (contexto.state === 'suspended') contexto.resume();
        return contexto;
    } catch {
        return null;
    }
}

/**
 * Una nota. `forma` da el carácter: 'sine' es redonda y cara, 'triangle' suena
 * a interfaz, 'square' a máquina. La envolvente (ataque corto, caída suave)
 * es lo que evita el «clic» de un tono que empieza y acaba de golpe.
 */
function nota(hz, { retraso = 0, duracion = 0.12, forma = 'sine', volumen = 1, hasta = null } = {}) {
    const ctx = motor();
    if (!ctx) return;
    try {
        const t = ctx.currentTime + retraso;
        const osc = ctx.createOscillator();
        const gan = ctx.createGain();
        osc.type = forma;
        osc.frequency.setValueAtTime(hz, t);
        if (hasta !== null) osc.frequency.exponentialRampToValueAtTime(hasta, t + duracion);
        gan.gain.setValueAtTime(0.0001, t);
        gan.gain.exponentialRampToValueAtTime(volumen, t + 0.012);
        gan.gain.exponentialRampToValueAtTime(0.0001, t + duracion);
        osc.connect(gan);
        gan.connect(maestro);
        osc.start(t);
        osc.stop(t + duracion + 0.02);
    } catch { /* un sonido que falla nunca rompe la pantalla */ }
}

/* ── El repertorio ────────────────────────────────────────────────────────── */

/** Tocar algo: una tarjeta, una ficha, una palabra. Casi un chasquido. */
export function toque() {
    nota(1180, { duracion: 0.05, forma: 'triangle', volumen: 0.5 });
}

/** Elegir: el toque, pero confirmado con una segunda nota más alta. */
export function elige() {
    nota(740, { duracion: 0.07, forma: 'triangle', volumen: 0.6 });
    nota(1108, { retraso: 0.055, duracion: 0.09, forma: 'triangle', volumen: 0.45 });
}

/** Avanzar de pantalla: dos notas que suben, como una puerta que se abre. */
export function avanza() {
    nota(523, { duracion: 0.1, forma: 'sine', volumen: 0.75 });
    nota(784, { retraso: 0.075, duracion: 0.16, forma: 'sine', volumen: 0.6 });
}

/** Acierto: arpegio mayor corto y brillante. */
export function acierto() {
    nota(880, { duracion: 0.09, forma: 'triangle', volumen: 0.75 });
    nota(1108, { retraso: 0.07, duracion: 0.09, forma: 'triangle', volumen: 0.65 });
    nota(1318, { retraso: 0.14, duracion: 0.2, forma: 'sine', volumen: 0.55 });
}

/**
 * Fallo. Grave, corto y sin disonancia: el documento maestro prohíbe que el
 * error se sienta como un castigo, y un sonido áspero lo convierte en uno.
 */
export function fallo() {
    nota(320, { duracion: 0.16, forma: 'sine', volumen: 0.6, hasta: 220 });
}

/** El cronómetro se agota. Un aviso, no una alarma. */
export function seAcaba() {
    nota(420, { duracion: 0.12, forma: 'triangle', volumen: 0.45 });
}

/** La revelación del plan: cinco notas ascendentes, el momento de la función. */
export function revela() {
    const escala = [523, 659, 784, 988, 1318];
    escala.forEach((hz, i) => {
        nota(hz, {
            retraso: i * 0.085,
            duracion: i === escala.length - 1 ? 0.5 : 0.16,
            forma: i === escala.length - 1 ? 'sine' : 'triangle',
            volumen: 0.6,
        });
    });
}

/** Arranque de la calibración: un barrido corto hacia arriba. */
export function arranca() {
    nota(300, { duracion: 0.28, forma: 'sine', volumen: 0.7, hasta: 900 });
}

/**
 * Un solo punto de entrada para el delegador de clics del diagnóstico, para no
 * tener que enganchar el sonido botón por botón — lo que garantizaría que el
 * próximo botón que alguien añada salga mudo.
 */
export function sonidoDeElemento(el) {
    if (!el) return;
    // Un componente puede reclamar su propio sonido marcando el botón con
    // `data-sonido="propio"`; si no, sonarían los dos a la vez y se emborronan.
    if (el.closest('[data-sonido="propio"]')) return;
    if (el.closest('.ob-btn')) return avanza();
    if (el.closest('.ob-card, .ob-choice, .ob-tone, .ob-chip, .ob-word, .ob-audio-btn')) return toque();
    if (el.closest('button, a')) return toque();
}
